import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, ChevronRight, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import DemoRequestModal from "../components/DemoRequestModal";

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const pricingPlans = [
  {
    name: "For Medical Practices",
    price: "$99",
    period: "/month",
    description: "Starting at",
    features: [
      "Provider management",
      "Document storage",
      "Credential tracking",
      "Expiration alerts",
      "Basic reporting"
    ],
    featured: false
  },
  {
    name: "For RCM Companies",
    price: "$199",
    period: "/month",
    description: "Starting at",
    features: [
      "Everything in Practices plan",
      "Multi-client management",
      "Claims & denial tracking",
      "AR aging reports",
      "Team collaboration",
      "Plus per provider and per user pricing"
    ],
    featured: true
  },
  {
    name: "Enterprise",
    price: "Custom",
    period: "",
    description: "For large organizations",
    features: [
      "Everything in RCM plan",
      "Unlimited providers",
      "Custom integrations",
      "API access",
      "Dedicated support",
      "Custom onboarding"
    ],
    featured: false
  }
];

const addOns = [
  "E-signatures",
  "AI features",
  "Advanced analytics",
  "API access"
];

export default function Pricing() {
  const [demoModalOpen, setDemoModalOpen] = useState(false);

  const handleGetStarted = () => {
    setDemoModalOpen(true);
  };

  return (
    <>
      <DemoRequestModal isOpen={demoModalOpen} onClose={() => setDemoModalOpen(false)} />

      {/* Hero */}
      <section className="pt-28 pb-8 sm:pt-32 sm:pb-10 md:pt-36 md:pb-12 px-4 sm:px-6" data-testid="pricing-hero">
        <div className="max-w-7xl mx-auto text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.p 
              variants={fadeInUp}
              className="text-xs tracking-[0.15em] uppercase font-bold text-[#8A9A86] mb-3"
            >
              Pricing
            </motion.p>
            <motion.h1 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-[#1A362D] font-medium mb-4"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Simple, Scalable Pricing
            </motion.h1>
            <motion.p 
              variants={fadeInUp}
              className="text-base sm:text-lg text-[#5C6C65] max-w-xl mx-auto"
            >
              Choose the plan that fits your organization. Scale as you grow.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-8 sm:py-10 md:py-12 px-4 sm:px-6" data-testid="pricing-cards">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-6 items-stretch"
          >
            {pricingPlans.map((plan, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className={`rounded-2xl p-6 sm:p-8 flex flex-col ${
                  plan.featured 
                    ? 'bg-[#1A362D] text-white md:-mt-4 md:mb-4 shadow-xl' 
                    : 'bg-white border border-[#E2E8D8]'
                }`}
                data-testid={`pricing-plan-${index}`}
              >
                {plan.featured && (
                  <div className="text-xs tracking-[0.15em] uppercase font-bold text-[#C87961] mb-3">
                    Most Popular
                  </div>
                )}
                <h3 
                  className={`text-lg font-medium mb-1.5 ${plan.featured ? 'text-white' : 'text-[#1A362D]'}`}
                  style={{ fontFamily: "'Cormorant Garamond', serif" }}
                >
                  {plan.name}
                </h3>
                <p className={`text-xs mb-4 ${plan.featured ? 'text-white/70' : 'text-[#5C6C65]'}`}>
                  {plan.description}
                </p>
                <div className="mb-5">
                  <span className={`text-4xl font-bold ${plan.featured ? 'text-white' : 'text-[#1A362D]'}`}>
                    {plan.price}
                  </span>
                  <span className={plan.featured ? 'text-white/70' : 'text-[#5C6C65]'}>
                    {plan.period}
                  </span>
                </div>
                <ul className="space-y-2.5 mb-6 flex-grow">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <Check size={16} className={`mt-0.5 flex-shrink-0 ${plan.featured ? 'text-[#C87961]' : 'text-[#8A9A86]'}`} />
                      <span className={`text-sm ${plan.featured ? 'text-white' : 'text-[#5C6C65]'}`}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>
                <Button 
                  className={`w-full btn-pill py-5 ${plan.featured ? 'bg-white text-[#1A362D] hover:bg-gray-100' : 'btn-primary'}`}
                  onClick={handleGetStarted}
                  data-testid={`pricing-cta-${index}`}
                >
                  {plan.name === 'Enterprise' ? 'Contact Sales' : 'Book a Demo'}
                </Button>
              </motion.div>
            ))}
          </motion.div>

          {/* Add-ons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-10 text-center"
          >
            <p className="text-sm text-[#5C6C65] mb-3">Available add-ons:</p>
            <div className="flex flex-wrap justify-center gap-3">
              {addOns.map((addon, index) => (
                <span key={index} className="bg-[#F9F6F0] text-[#1A362D] text-sm px-4 py-2 rounded-full border border-[#E2E8D8]">
                  {addon}
                </span>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Link */}
      <section className="py-10 sm:py-12 px-4 sm:px-6 bg-white">
        <div className="max-w-2xl mx-auto text-center">
          <h2 
            className="text-xl sm:text-2xl tracking-tight text-[#1A362D] font-medium mb-3"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Have Questions?
          </h2>
          <p className="text-sm text-[#5C6C65] mb-5">
            Check out our FAQ for answers to common questions about pricing and features.
          </p>
          <Link to="/faq">
            <Button variant="outline" className="btn-pill btn-outline px-6 py-4">
              View FAQ
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-14 md:py-16 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-gradient-to-br from-[#1A362D] to-[#2B4D42] rounded-2xl p-8 sm:p-12 text-center"
          >
            <h2 
              className="text-2xl sm:text-3xl tracking-tight leading-tight text-white font-medium mb-4"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Get Started in Minutes
            </h2>
            <p className="text-base text-[#8A9A86] max-w-xl mx-auto mb-6">
              No setup headache. See how Praqio can work for your team.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                className="btn-pill btn-accent px-8 py-5"
                onClick={() => setDemoModalOpen(true)}
              >
                Book a Demo
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
              <Button 
                variant="outline"
                className="btn-pill border-white/30 text-white hover:bg-white/10 px-8 py-5"
                onClick={() => setDemoModalOpen(true)}
              >
                Get Started
              </Button>
            </div>

            <p className="text-xs text-[#8A9A86] mt-6">
              A product of <span className="text-white font-medium">Capital Billing Services, Inc.</span>
            </p>
          </motion.div>
        </div>
      </section>
    </>
  );
}
